/* ============================================================
   EQUIPIFY Slate & Teal — PDP enhancements (dependency-free).
   Ported from the design prototype's app.js:
   - Document buttons: finds spec-sheet / manual PDF links inside
     the product description HTML, renders them as styled buttons,
     and hides the raw links (incl. image links) in the copy.
   - Spec table: parses dimensions / electrical / NEMA / warranty
     from the description text. Parsed values are labeled as
     derived; class-typical defaults are labeled "typ. — confirm".
   - Warranty badges.
   - Freight preferences: ZIP + liftgate / inside delivery saved
     to cart attributes as a freight-quote request (no invented
     dollar amounts — freight is quoted with the order).
   - Sticky mobile quick-bar (SKU, spec PDF, quote CTA).
   ============================================================ */
(function () {
  'use strict';

  var ICON_DOC =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 3h8l5 5v13H6z"/><path d="M14 3v5h5"/><line x1="9" y1="13" x2="16" y2="13"/><line x1="9" y1="17" x2="16" y2="17"/></svg>';
  var ICON_SHIELD =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v6c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6z"/><path d="M9 12l2.2 2.2L15.5 10"/></svg>';

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ---------------- spec parsing (ported from prototype) ---------------- */

  function parseFrac(str) {
    var m = /^(\d+)(?:\s+(\d+)\/(\d+))?/.exec(String(str).trim());
    if (!m) return null;
    var v = parseInt(m[1], 10);
    if (m[2]) v += parseInt(m[2], 10) / parseInt(m[3], 10);
    return Math.round(v * 100) / 100;
  }

  function fmtIn(v) {
    if (v == null) return '—';
    var whole = Math.floor(v);
    var fr = v - whole;
    var fracs = [[0, ''], [0.125, '1/8'], [0.25, '1/4'], [0.375, '3/8'], [0.5, '1/2'], [0.625, '5/8'], [0.75, '3/4'], [0.875, '7/8'], [1, '']];
    var best = 0;
    var bd = 9;
    for (var i = 0; i < fracs.length; i++) {
      var d = Math.abs(fr - fracs[i][0]);
      if (d < bd) {
        bd = d;
        best = i;
      }
    }
    if (fracs[best][0] === 1) {
      whole += 1;
      best = 0;
    }
    return whole + (fracs[best][1] ? ' ' + fracs[best][1] : '') + '"';
  }

  // "49 3/8" x 27 3/4" x 36 1/2"" (W x D x H) from free text
  function parseDims(text) {
    var re = /(\d+(?:\s+\d+\/\d+)?(?:\.\d+)?)\s*(?:”|"|″|in\.?)?\s*[x×]\s*(\d+(?:\s+\d+\/\d+)?(?:\.\d+)?)\s*(?:”|"|″|in\.?)?\s*[x×]\s*(\d+(?:\s+\d+\/\d+)?(?:\.\d+)?)\s*(?:”|"|″|in\.?)/i;
    var m = re.exec(text);
    if (!m) return null;
    var w = parseFrac(m[1]);
    var d = parseFrac(m[2]);
    var h = parseFrac(m[3]);
    if (w == null || w < 4 || w > 200) return null;
    return { w: w, d: d, h: h };
  }

  // Utility / electrical extraction. Values flagged assumed:true are
  // class-typical defaults (NOT in the manufacturer copy) and every
  // surface that shows them labels them "typ. — confirm".
  function parseUtility(text) {
    var out = {
      type: null,
      nema: null,
      voltage: null,
      amps: null,
      hp: null,
      watts: null,
      btu: null,
      gasConn: null,
      refrigerant: null,
      nemaAssumed: false,
      anyAssumed: false
    };
    var gasHit = /\bNPT\b|BTU|propane|natural gas|LP gas/i.test(text);
    var nm = /NEMA\s*(\d+-\d+)\s*P?/i.exec(text);
    if (nm) out.nema = nm[1].toUpperCase() + 'P';
    var v = /(\d{3})\s*V(?:olts?|\/60|\b)/i.exec(text);
    if (v) out.voltage = v[1] + 'V/60Hz';
    var a = /([\d.]+)\s*Amps?\b/i.exec(text);
    if (a) out.amps = a[1] + ' A';
    var hp = /([\d/]+)\s*HP\b/i.exec(text);
    if (hp) out.hp = hp[1] + ' HP';
    var w = /([\d,]{2,6})\s*watts?\b/i.exec(text);
    if (w) out.watts = w[1] + ' W';
    var b = /([\d,]{3,7})\s*BTU/i.exec(text);
    if (b) out.btu = b[1] + ' BTU';
    var refr = /\bR-?(290|404A?|134A?|600A?|513A?)\b/i.exec(text);
    if (refr) out.refrigerant = 'R' + refr[1].toUpperCase();

    if (gasHit) {
      out.type = 'gas';
      var g = /(\d\/\d)\s*(?:”|"|″)?\s*NPT/i.exec(text);
      if (g) out.gasConn = g[1] + '" NPT connection';
    } else if (out.nema || out.voltage || out.amps || out.watts || out.hp || out.refrigerant) {
      out.type = 'electric';
      if (!out.nema && out.voltage && parseInt(out.voltage, 10) < 200) {
        // typical for 115V-class equipment — labeled, confirmed at order review
        out.nema = '5-15P';
        out.nemaAssumed = true;
        out.anyAssumed = true;
      }
    }
    return out;
  }

  function parseWarranty(text) {
    var out = [];
    var seen = {};
    var re = /(\d+)\s*[-–]?\s*year[s]?(?:\s+of)?\s+((?:parts\s*(?:&(?:amp;)?|and)\s*labou?r|parts|labou?r|compressor|warranty)(?:\s+(?:warranty|coverage))?)/gi;
    var m;
    while ((m = re.exec(text)) !== null && out.length < 4) {
      var label = m[1] + ' Year ' + m[2].replace(/\s+/g, ' ').replace(/warranty\s*warranty/i, 'Warranty');
      label = label.replace(/\b\w/g, function (c) {
        return c.toUpperCase();
      });
      if (!seen[label.toLowerCase()]) {
        seen[label.toLowerCase()] = true;
        out.push(label);
      }
    }
    return out;
  }

  /* ---------------- document buttons ---------------- */

  function classifyDoc(href, text) {
    var t = (text + ' ' + href).toLowerCase();
    if (t.indexOf('spec') >= 0) return 'Spec Sheet (PDF)';
    if (t.indexOf('manual') >= 0 || t.indexOf('owner') >= 0 || t.indexOf('instruction') >= 0) return 'Manual (PDF)';
    if (t.indexOf('warranty') >= 0) return 'Warranty (PDF)';
    if (t.indexOf('brochure') >= 0) return 'Brochure (PDF)';
    return 'Document (PDF)';
  }

  function extractDocs(descEl, docsEl) {
    var found = [];
    if (!descEl) return found;
    var anchors = descEl.querySelectorAll('a[href]');
    Array.prototype.forEach.call(anchors, function (anchorEl) {
      var href = anchorEl.getAttribute('href') || '';
      if (!/\.pdf(\?|#|$)/i.test(href)) return;
      var label = classifyDoc(href, anchorEl.textContent || '');
      var dup = found.some(function (d) {
        return d.href === href;
      });
      if (!dup) found.push({ href: href, label: label });
      // hide the raw link (covers image-links wrapped in <a> too)
      anchorEl.style.display = 'none';
      var parent = anchorEl.parentElement;
      if (parent && parent !== descEl) {
        var visible = Array.prototype.filter.call(parent.childNodes, function (n) {
          if (n.nodeType === 3) return n.textContent.trim().length > 0;
          if (n.nodeType === 1) return n.style ? n.style.display !== 'none' : true;
          return false;
        });
        if (!visible.length) parent.style.display = 'none';
      }
    });
    if (docsEl && found.length) {
      docsEl.innerHTML = found
        .map(function (d) {
          return '<a class="slate-dl" href="' + esc(d.href) + '" target="_blank" rel="noopener">' + ICON_DOC + esc(d.label) + '</a>';
        })
        .join('');
    }
    return found;
  }

  /* ---------------- spec table render ---------------- */

  function assumedTag() {
    return '<span class="slate-assumed" title="Not in the manufacturer copy — typical for this equipment class; confirmed at order review">typ. — confirm</span>';
  }

  function buildSpecs(root, text) {
    var panel = root.querySelector('[data-slate-specs]');
    if (!panel) return;
    var sku = root.getAttribute('data-slate-sku') || '';
    var vendor = root.getAttribute('data-slate-vendor') || '';
    var ptype = root.getAttribute('data-slate-type') || '';

    var dims = parseDims(text);
    var util = parseUtility(text);
    var warranty = parseWarranty(text);

    var rows = [];
    if (sku) rows.push(['Model #', sku]);
    if (vendor) rows.push(['Brand', vendor]);
    if (ptype) rows.push(['Category', ptype]);
    if (dims) {
      rows.push(['Width', fmtIn(dims.w), true]);
      if (dims.d != null) rows.push(['Depth', fmtIn(dims.d), true]);
      if (dims.h != null) rows.push(['Height', fmtIn(dims.h), true]);
    }
    if (util.type === 'electric') {
      if (util.voltage) rows.push(['Voltage', util.voltage, true]);
      if (util.nema) rows.push(['Plug', 'NEMA ' + util.nema, true, util.nemaAssumed]);
      if (util.amps) rows.push(['Amps', util.amps, true]);
      if (util.hp) rows.push(['Compressor', util.hp, true]);
      if (util.watts) rows.push(['Power draw', util.watts, true]);
    } else if (util.type === 'gas') {
      rows.push(['Utility', 'Gas — NG or LP', true]);
      if (util.gasConn) rows.push(['Connection', util.gasConn, true]);
      if (util.btu) rows.push(['Rating', util.btu, true]);
    }
    if (util.refrigerant) rows.push(['Refrigerant', util.refrigerant, true]);
    warranty.forEach(function (wl, i) {
      rows.push([i === 0 ? 'Warranty' : '', wl, true]);
    });

    // only show the panel when parsing found something beyond identity rows
    var parsedCount = rows.filter(function (r) {
      return r[2];
    }).length;
    if (parsedCount === 0) return;

    var anyAssumed = rows.some(function (r) {
      return r[3];
    });
    var half = Math.ceil(rows.length / 2);

    function tableHTML(list) {
      return (
        '<table class="slate-spectable">' +
        list
          .map(function (r) {
            return '<tr><th scope="row">' + esc(r[0]) + '</th><td>' + esc(r[1]) + (r[3] ? ' ' + assumedTag() : '') + '</td></tr>';
          })
          .join('') +
        '</table>'
      );
    }

    panel.innerHTML =
      '<h2>Specifications</h2>' +
      '<div class="slate-spec2col">' +
      tableHTML(rows.slice(0, half)) +
      tableHTML(rows.slice(half)) +
      '</div>' +
      '<p class="slate-note">Values above are parsed automatically from the manufacturer description — verify against the spec sheet PDF before install.' +
      (anyAssumed
        ? ' Values marked <span class="slate-assumed">typ. — confirm</span> are typical for this equipment class, not published in the manufacturer copy — we confirm them with you at order review.'
        : '') +
      '</p>';
    panel.hidden = false;

    // warranty badges
    var warEl = root.querySelector('[data-slate-warranty]');
    if (warEl && warranty.length) {
      warEl.innerHTML = warranty
        .map(function (wl) {
          return '<span class="slate-warbadge">' + ICON_SHIELD + esc(wl) + '</span>';
        })
        .join('');
    }
    return { dims: dims, util: util };
  }

  /* ---------------- freight preferences → cart attributes ---------------- */

  function initFreight(root) {
    var box = root.querySelector('[data-slate-freight]');
    if (!box) return;
    var zip = box.querySelector('[data-slate-freight-zip]');
    var lift = box.querySelector('[data-slate-freight-liftgate]');
    var inside = box.querySelector('[data-slate-freight-inside]');
    var saved = box.querySelector('[data-slate-freight-saved]');
    var timer = null;

    function updateUrl() {
      return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    }

    function save() {
      var attrs = {
        'Delivery ZIP': zip ? zip.value.trim() : '',
        'Liftgate service': lift && lift.checked ? 'Yes' : 'No',
        'Inside delivery': inside && inside.checked ? 'Yes' : 'No'
      };
      fetch(updateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      })
        .then(function (r) {
          if (r.ok && saved) {
            saved.classList.add('is-on');
            setTimeout(function () {
              saved.classList.remove('is-on');
            }, 2200);
          }
        })
        .catch(function () {});
    }

    function debounced() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(save, 600);
    }

    if (zip) zip.addEventListener('input', debounced);
    if (lift) lift.addEventListener('change', save);
    if (inside) inside.addEventListener('change', save);
  }

  /* ---------------- sticky mobile quick-bar ---------------- */

  function initMobilebar(root, docs) {
    var bar = root.querySelector('[data-slate-mobilebar]');
    if (!bar) return;
    var specDoc = (docs || []).filter(function (d) {
      return d.label.indexOf('Spec') === 0;
    })[0] || (docs || [])[0];
    var slot = bar.querySelector('[data-slate-mobilebar-doc]');
    if (slot && specDoc) {
      slot.innerHTML = '<a class="slate-dl" href="' + esc(specDoc.href) + '" target="_blank" rel="noopener">Spec PDF</a>';
    }
    var cta = bar.querySelector('[data-slate-mobilebar-cta]');
    if (cta) {
      cta.addEventListener('click', function () {
        var submit = document.querySelector('.product-form__submit');
        if (submit && !submit.disabled) {
          submit.click();
        } else {
          var info = document.querySelector('.product__info-container');
          if (info) info.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
    bar.hidden = false;
    var shown = false;
    window.addEventListener(
      'scroll',
      function () {
        var s = window.scrollY > 420;
        if (s !== shown) {
          shown = s;
          bar.classList.toggle('is-shown', s);
        }
      },
      { passive: true }
    );
  }

  /* ---------------- init ---------------- */

  function init() {
    var root = document.querySelector('[data-slate-pdp]');
    if (!root) return;
    var desc = document.querySelector('.product__description');
    var docsEl = root.querySelector('[data-slate-docs]');
    var docs = extractDocs(desc, docsEl);
    var text = desc ? desc.textContent || '' : '';
    buildSpecs(root, text);
    initFreight(root);
    initMobilebar(root, docs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
